/**
 * @ngdoc services
 * @memberof mercury.modules.reports
 * @name ReportsCogsPosItemLinkService
 */
angular.module('mercury.modules.reports.cogs')
.factory('ReportsCogsPosItemLinkService', ["$q", "$http", "settings", "StockCost", function($q, $http, settings, StockCost) {
  // public and private funcitons
  var public = {};

  // fetched data
  public.posSales = [];
  public.allRecipes = [];
  public.allStockItems = [];
  public.posItemLines = [];

  // cache previously fetched item costs
  var itemCosts = {};

  public.clearAll = function() {
    public.posSales = [];
    public.allRecipes = [];
    public.allStockItems = [];
    public.posItemLines = [];
    itemCosts = {};
  };

  /**
    * Gets the greater of the ValidFrom time of the Link, or the start of the report.
    *
    * We only want to display Cogs for the time in the report, so we need to restrict
    *   the startTime that we query for to the greater of either the report start time, or the link valid from time
    * @memberof ReportsCogsPosItemLinkService
    * @param {Object} posItemLink source pos item link
    * @param {Date} startTime report start time
    */
  public.getLinkStartTime = function(posItemLink, startTime) {
    if (posItemLink.validFrom && moment(posItemLink.validFrom).isAfter(moment(startTime))) {
      return posItemLink.validFrom;
    } else {
      return startTime;
    }
  };

  /**
    * Gets the smaller of the ValidUntil time of the Link, or the end of the report.
    *
    * We only want to display Cogs for the time in the report, so we need to restrict
    *   the startTime that we query for to the greater of either the report start time, or the link valid from time
    * @memberof ReportsCogsPosItemLinkService
    * @param {Object} posItemLink source pos item link
    * @param {Date} endTime report end time
    */
  public.getLinkEndTime = function(posItemLink, nextLink, endTime) {
    var posItemLinkValidUntil = nextLink ? nextLink.validFrom : null;

    if (posItemLinkValidUntil && moment(posItemLinkValidUntil).isBefore(moment(endTime))) {
      return nextLink.validFrom;
    } else {
      return endTime;
    }
  };

  /**
    * Performs the request to get the Stock Cost.

    * @memberof ReportsCogsPosItemLinkService
    * @param {Object} itemId item to get the stock cost for
    * @param {Date} dateFor report end time (all stock costs are calculated as at the end date of the report)
    */
  var requestStockCost = function(itemId, dateFor) {    
    var defer = $q.defer()
    var itemIdTimeString = `${itemId},${dateFor}`
    StockCost.get([itemIdTimeString],[]).then(costs => {
      var itemCost = costs.itemCosts[itemId][0]
      return defer.resolve(itemCost)
    })
    return defer.promise
  };

  /**
    * Performs the request to get the Stock Cost for a recipe.

    * @memberof ReportsCogsPosItemLinkService
    * @param {Object} recipeId recipe to get the stock cost for
    * @param {Date} dateFor report end time (all stock costs are calculated as at the end date of the report)
    */
  var requestRecipeCost = function(recipeId, dateFor) {
    var defer = $q.defer()
    var recipeIdTimeString = `${recipeId},${dateFor}`
    StockCost.get([],[recipeIdTimeString]).then(costs => {
      var recipeCost = costs.recipeCosts[recipeId][0]
      return defer.resolve(recipeCost) 
    })
    return defer.promise
  };

  /**
   * Gets total sales for pos sales of the pos item being viewed for a time period.
   *
   * @memberof ReportsCogsPosItemLinkService
   * @param {*} startTime the start time (inclusive) to get sales from
   * @param {*} endTime the end time (exclusive) to get sales before
   * @returns {Int} Sum of sales for pos sales for the item over the requested period.
   */
  var getCogsSales = function(startTime, endTime) {
    var qtyTotal = 0;
    angular.forEach(public.posSales, function(posSale) {
      if (!moment(posSale.startTime).isBefore(moment(startTime)) &&
      moment(posSale.startTime).isBefore(moment(endTime))) {
        qtyTotal += posSale.quantity;
      }
    });

    return qtyTotal;
  };

  /**
    * Update the stock cost and COGS for a pos item link
    *
    * @memberof ReportsCogsPosItemLinkService
    * @param {Object} posItemLink Link information
    */
  public.calculateCogsForItem = function(posItemLink, cogsStartTime, cogsEndTime) {
    var deferred = $q.defer();

    if (itemCosts[(posItemLink.recipeId || posItemLink.stockItemId)]) {
      var costs = itemCosts[(posItemLink.recipeId || posItemLink.stockItemId)];
      costs.quantitySold = getCogsSales(cogsStartTime, cogsEndTime);
      deferred.resolve(costs);
    } else {
      if (posItemLink.recipeId === null && posItemLink.stockItemId === null) {
        deferred.resolve(null);
        return deferred.promise;
      }

      if (posItemLink.recipeId) {
        requestRecipeCost(posItemLink.recipeId, cogsEndTime).then(function(result) {
          result.quantitySold = getCogsSales(cogsStartTime, cogsEndTime);
          itemCosts[posItemLink.recipeId] = result;
          deferred.resolve(result);
        });
      } else {
        requestStockCost(posItemLink.stockItemId, cogsEndTime).then(function(result) {
          result.quantitySold = getCogsSales(cogsStartTime, cogsEndTime);
          itemCosts[posItemLink.stockItemId] = result;
          deferred.resolve(result);
        });
      }
    }

    return deferred.promise;
  };

  /**
   * Calculate the COGs for a a single POS item link.
   * NOTE: Do NOT use if needing to calculate for multiple links. Use calculateCogsForLinks
   * @param {*} posItemLink the link to calculate the COGs of
   * @param {*} reportStartTime the start of the reporting period
   * @param {*} reportEndTime the end of the reporting period
   */
  public.calculateCogsForLink = function(posItemLink, reportStartTime, reportEndTime) {
    var cogsStartTime = public.getLinkStartTime(posItemLink, reportStartTime);
    var cogsEndTime = public.getLinkEndTime(posItemLink, getNextPosLink(posItemLink.lineIndex), reportEndTime);

    posItemLink.cogsPromise = public.calculateCogsForItem(posItemLink, cogsStartTime, cogsEndTime);
    posItemLink.cogsPromise.then(function(cogsData) {
      setPosItemLinkCogsFields(posItemLink, cogsData)
    });
  };

  /**
   * Calculate the COGs for multiple POS item links. 
   * NOTE: This should be used over calculateCogsForLink calculating for multiple links.
   * This method does gets the stock cost in one request rather than one request per item/recipe 
   * @param {*} posItemLinks the links to calculate the COGs of
   * @param {*} reportStartTime the start of the reporting period
   * @param {*} reportEndTime the end of the reporting period
   */
  public.calculateCogsForLinks = function(posItemLinks, reportStartTime, reportEndTime) {
    var deferred = $q.defer();

    var recipeIdTimeStrings = [];
    var itemIdTimeStrings = [];

    // Iterate over all links to get their costs at the relevant times
    posItemLinks.forEach(function(link) {
      var cogsEndTime = public.getLinkEndTime(link, getNextPosLink(posItemLinks, link.lineIndex), reportEndTime);

      if (link.recipeId) recipeIdTimeStrings.push(
        `${link.recipeId},${cogsEndTime}`
      )
      if (link.stockItemId) itemIdTimeStrings.push(
        `${link.stockItemId},${cogsEndTime}`
      )
    })

    StockCost.get(itemIdTimeStrings, recipeIdTimeStrings)
    .then(function(stockCosts) {
      // Iterate over all linke to apply the COGs data to them
      posItemLinks.forEach(function(link) {
        var cogsStartTime = public.getLinkStartTime(link, reportStartTime);
        var cogsEndTime = public.getLinkEndTime(link, getNextPosLink(posItemLinks, link.lineIndex), reportEndTime);
  
        var stockCostForLink = retrieveStockCostForLink(stockCosts, link, cogsEndTime)

        stockCostForLink.quantitySold = getCogsSales(cogsStartTime, cogsEndTime);
        itemCosts[link.recipeId || link.stockItemId] = stockCostForLink;
        
        setPosItemLinkCogsFields(link, stockCostForLink)
      })
      return deferred.resolve(stockCosts)
    });

    return deferred.promise
  }

  /**
   * Sets all the COGs data on a link, once the stockCost has been retrieved (For use with the StockAPI StockCost endpoint)
   * @param {*} posItemLink The link to set the COGs data on
   * @param {*} cogsData The COGs data
   */
  var setPosItemLinkCogsFields = (posItemLink, cogsData) => {
    posItemLink.quantitySold = cogsData.quantitySold;
    posItemLink.stockCost = cogsData.cost;
    posItemLink.stockUnitId = cogsData.unitId;
    posItemLink.stockUnitName = cogsData.unitName;
    posItemLink.stockUnitRatio = cogsData.unitRatio;
    posItemLink.stockCostDocumentId = cogsData.stockCostDocumentId;
    posItemLink.stockCostDocumentType = cogsData.stockCostDocumentType;
    posItemLink.cogsTotalCost = cogsData.cost * posItemLink.quantity * cogsData.quantitySold;
    if (posItemLink.recipeId) {
      // We can end up with these values unset when adding a recipe pos item
      // link, in this case it's safe to take the values off the stock item.
      if (posItemLink.unitId === undefined) {
        posItemLink.unitId = cogsData.unitId;
      }
      if (posItemLink.unitName === undefined) {
        posItemLink.unitName = cogsData.unitName;
      }
      if (posItemLink.unitRatio === undefined) {
        posItemLink.unitRatio = cogsData.unitRatio;
      }
    }
  }

  /**
   * Retrieves the relvant stock cost object for a given link
   * @param {*} stockCosts the resposne from the stock cost endpoint
   * @param {*} link link we are trying to match the stock cost too
   * @param {*} cogsEndTime the COGs end time
   * @returns the relevant stock cost for the given link
   */
  var retrieveStockCostForLink = (stockCosts, link, cogsEndTime) => {     
    var costsForStock = link.stockItemId 
      ? stockCosts.itemCosts[link.stockItemId]
      : stockCosts.recipeCosts[link.recipeId]
    
    if (costsForStock.length == 1) return costsForStock[0]

    var endTimeAsMoment = moment(cogsEndTime)
    return costsForStock.find(cost => moment(cost.at).isSame(endTimeAsMoment));
  }

  /**
   * Finds the next non-deleted posLink in the list (if any)
   * @param {*} posItemLinks the PosItemLinks to search for 
   * @param {*} index the index of the current element
   * @returns the next pos link or null
   */
  var getNextPosLink = (posItemLinks, index) => {
    var nextIndex = getNextPosLinkIndex(posItemLinks, index)
    if (nextIndex !== null) {
      return posItemLinks[nextIndex];
    }
    return null;
  }

  /**
   * Finds the index of the next non-deleted posLink in the list (if any)
   * @param {*} posItemLinks the posItemLinks to search
   * @param {*} index the index of the current element
   * @returns the index or null (if it doesnt exist)
   */
  var getNextPosLinkIndex = (posItemLinks, index) => {
    for (var i = index + 1; i < posItemLinks.length; i++){
      if (!posItemLinks[i].deleted) {
        return i
      }
    }
    return null
  }

  /**
    * Builds a valid pos item link display variables object.
    *
    * @memberof ReportsCogsPosItemLinkService
    * @param {Object} posItemLink Link information
    * @param {integer} index line index
    */
  public.makeLink = function(posItemLink, index) {
    return {
      lineIndex: index,
      id: posItemLink.id,
      posIdentifier: posItemLink.posIdentifier,
      recipeId: posItemLink.recipeId,
      stockItemId: posItemLink.stockItemId,
      stockName: posItemLink.stockName,
      unitId: posItemLink.unitId,
      unitName: posItemLink.unitName,
      unitRatio: posItemLink.unitRatio,
      validFrom: posItemLink.validFrom,
      quantity: posItemLink.quantity,
      cogsQuantitySold: null,
      cogsUnitCost: null,
      cogsTotalCost: null
    };
  };

  return public;
}]);
